import { Check } from "lucide-react";
import { ReactNode } from "react";


export interface TableSelectorItem {
  _id: string;
}


interface TableSelectorProps<T extends TableSelectorItem> {
  items: T[];
  selectedItems: T[];
  onItemToggle: (item: T) => void;
  renderContent: (item: T, isSelected: boolean) => ReactNode;
  getItemClassName?: (item: T, isSelected: boolean) => string;
}

export function TableSelector<T extends TableSelectorItem>({
  items,
  selectedItems,
  onItemToggle,
  renderContent,
  getItemClassName
}: TableSelectorProps<T>) {

  const defaultItemClassName = (_item: T, isSelected: boolean) => {
    return `group relative transition-all duration-200 cursor-pointer
      ${isSelected
        ? "bg-gradient-to-r from-blue-50 to-blue-25 border-l-4 border-blue-500 shadow-sm"
        : "hover:bg-gray-50 hover:shadow-sm"
      }
    `;
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="divide-y divide-gray-100">
        {items.map((item) => {
          const isSelected = selectedItems.some(selected => selected._id === item._id);
          const itemClassName = getItemClassName ? getItemClassName(item, isSelected) : defaultItemClassName(item, isSelected);

          return (
            <div
              key={item._id}
              className={itemClassName}
              onClick={() => onItemToggle(item)}
              tabIndex={0}
              onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onItemToggle(item);
                }
              }}
              aria-checked={isSelected}
              role="checkbox"
            >
              <div className="flex items-center px-6 py-4">
                {/* Checkbox personalizado */}
                <div className="relative flex items-center">
                  <input
                    type="checkbox"
                    id={item._id}
                    checked={isSelected}
                    onChange={() => onItemToggle(item)}
                    className="sr-only"
                    tabIndex={-1}
                  />
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-200
                    ${isSelected
                      ? "bg-blue-500 border-blue-500"
                      : "border-gray-300 group-hover:border-gray-400"
                    }
                  `}>
                    {isSelected && (
                      <Check className="w-3 h-3 text-white" />
                    )}
                  </div>
                </div>

                {/* Conteúdo customizável */}
                <label htmlFor={item._id} className="flex-1 flex items-center ml-4 cursor-pointer select-none">
                  {renderContent(item, isSelected)}
                </label>

                {/* Indicador visual de seleção */}
                {isSelected && (
                  <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                  </div>
                )}
              </div>

              {/* Efeito de hover sutil */}
              <div className={`absolute inset-0 pointer-events-none transition-opacity duration-200
                ${isSelected ? "opacity-0" : "opacity-0 group-hover:opacity-5 bg-blue-500"}
              `}></div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
